import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Area, Line,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { RangeSelector, useTimeRange } from '../data/TimeRange';

// Gold primary + green moving average. warm/cool keep a red↔teal diverging
// pair (temperature is inherently warm/cool) but off the brand-blue.
const COLORS = {
  delta: '#FFB800',
  movingAvg: '#10B981',
  warm: '#E2604F',
  cool: '#5FA8C7',
};

const formatDelta = (v) => {
  if (v == null) return '--';
  const num = typeof v === 'string' ? parseFloat(v) : v;
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}°`;
};

const TemperatureChart = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, spanDays, selectorProps } = useTimeRange();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/temperature');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load temperature data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    return allData.filter(d => inRange(d.date));
  }, [allData, inRange]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const deltaVals = filteredData.filter(d => d.temp_delta != null).map(d => d.temp_delta);
    const latest = filteredData[filteredData.length - 1];
    const warmNights = deltaVals.filter(v => v > 0.5).length;
    const coolNights = deltaVals.filter(v => v < -0.5).length;
    return {
      latestDelta: latest.temp_delta,
      avgDelta: deltaVals.length ? +(deltaVals.reduce((a, b) => a + b, 0) / deltaVals.length).toFixed(2) : null,
      minDelta: deltaVals.length ? +Math.min(...deltaVals).toFixed(2) : null,
      maxDelta: deltaVals.length ? +Math.max(...deltaVals).toFixed(2) : null,
      warmNights,
      coolNights,
      totalEntries: filteredData.length,
    };
  }, [filteredData]);

  const chartData = useMemo(() => {
    if (!filteredData.length) return [];
    const windowSize = filteredData.length > 90 ? 14 : 7;

    return filteredData.map((d, i) => {
      const window = filteredData.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.temp_delta != null).map(w => w.temp_delta);
      const movingAvg = avgVals.length >= 2
        ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(2)
        : null;

      return {
        date: d.date,
        delta: d.temp_delta != null ? +d.temp_delta.toFixed(2) : null,
        movingAvg,
      };
    });
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-600 dark:text-gray-300">
            No temperature data available. This requires a Fitbit device with skin temperature tracking.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = spanDays;
    if (days && days <= 30) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const singleDay = spanDays === 1;

  const TempTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.delta != null && (
            <p style={{ color: d.delta >= 0 ? COLORS.warm : COLORS.cool }}>
              Variation: <span className="font-semibold">{formatDelta(d.delta)}</span>
            </p>
          )}
          {d.movingAvg != null && (
            <p style={{ color: COLORS.movingAvg }}>
              Moving Avg: <span className="font-semibold">{formatDelta(d.movingAvg)}</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const latestColor = stats?.latestDelta != null
    ? (stats.latestDelta >= 0 ? COLORS.warm : COLORS.cool)
    : undefined;

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-gray-600 dark:text-gray-300">
            {stats?.totalEntries} nights with data
          </span>
        )}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Latest</p>
              <p className="text-2xl font-bold" style={{ color: latestColor }}>
                {stats?.latestDelta != null ? formatDelta(stats.latestDelta) : '--'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">vs baseline</p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Average</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.avgDelta != null ? formatDelta(stats.avgDelta) : '--'}
              </p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Range</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.minDelta != null ? `${formatDelta(stats.minDelta)}` : '--'}
                {stats?.maxDelta != null ? ` to ${formatDelta(stats.maxDelta)}` : ''}
              </p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Notable Nights</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm" style={{ color: COLORS.warm }}>
                  {stats?.warmNights ?? 0} warm
                </span>
                <span className="text-sm" style={{ color: COLORS.cool }}>
                  {stats?.coolNights ?? 0} cool
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">&gt; ±0.5° from baseline</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Temperature Variation Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Skin Temperature Variation</CardTitle>
            <span className="text-sm text-gray-400">Relative to your baseline</span>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={['dataMin - 0.5', 'dataMax + 0.5']}
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v}°`}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<TempTooltip />} />
              <Legend
                formatter={(value) => {
                  const map = { delta: 'Variation', movingAvg: 'Moving Avg' };
                  return map[value] || value;
                }}
                wrapperStyle={{ fontSize: 14 }}
              />
              <ReferenceLine
                y={0}
                stroke="#9ca3af"
                strokeWidth={1.5}
                label={{ value: 'Baseline', position: 'insideTopLeft', fill: '#9ca3af', fontSize: 14 }}
              />
              <ReferenceLine y={0.5} stroke={COLORS.warm} strokeDasharray="3 3" opacity={0.4} />
              <ReferenceLine y={-0.5} stroke={COLORS.cool} strokeDasharray="3 3" opacity={0.4} />
              <Area
                dataKey="delta"
                stroke={COLORS.delta}
                strokeWidth={2}
                fill={COLORS.delta}
                fillOpacity={0.1}
                type="monotone"
                dot={chartData.length <= 60 ? { r: 3, fill: COLORS.delta } : false}
                connectNulls
                name="delta"
              />
              <Line
                dataKey="movingAvg"
                stroke={COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
                strokeDasharray="5 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Fitbit measures nightly skin temperature relative to your personal baseline. Small variations (within ±0.5°) are normal. Larger shifts may indicate illness, environment changes, or other factors.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemperatureChart;
